'use client';

import { fetchClient } from '@shared/apis';
import { AUTH_URLS } from '@shared/constants/authUrls';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { pushGTMEvent, toBtnName } from '@/shared/utils/gtm';

/**
 * 가입 수단 — 시트 `method`.
 *
 * 소셜 최초 가입(`/sign-in/link`)은 인증 서버에서 어떤 소셜로 진입했는지
 * 앱으로 내려오지 않아 제공자(google/kakao 등)를 특정할 수 없다. 그래서 `social` 로 묶는다.
 */
export const SIGNUP_METHOD = {
  /** 자체 이메일·비밀번호 가입 */
  EMAIL: 'email',
  /** 소셜 sub 에 이메일 계정을 연동하는 최초 가입 */
  SOCIAL: 'social',
} as const;

export type SignUpMethod = (typeof SIGNUP_METHOD)[keyof typeof SIGNUP_METHOD];

/**
 * GA `user_id` 로 쓸 `authUserId` 조회.
 *
 * 가입 직후에는 `isLoggedIn` 쿠키가 없어 `useClientMe`(= `useGtmUserId`) 가 비활성이라
 * 여기서 직접 호출한다. accessToken 쿠키는 이미 심겨 있어 인증은 통과한다.
 * 실패해도 가입 집계 자체는 막지 않는다.
 */
async function fetchAuthUserId() {
  try {
    const { data } = await fetchClient.GET('/api/v1/client/me');
    return data?.authUserId;
  } catch {
    return undefined;
  }
}

/**
 * 완료 화면을 새로고침하면 펀넬이 history state 로 같은 스텝을 복원해 다시 마운트된다.
 * 컴포넌트 ref 가드로는 못 막으므로 탭 단위로 1회만 집계한다.
 */
const PUSHED_KEY = 'gtm_signup_pushed';

function markPushedOnce() {
  if (typeof sessionStorage === 'undefined') return true;
  if (sessionStorage.getItem(PUSHED_KEY)) return false;
  sessionStorage.setItem(PUSHED_KEY, '1');
  return true;
}

/**
 * 시트 row 39 `click_signup` — 가입 완료 시점에 발사한다.
 * 비고대로 "행동은 click 이나 호출은 성공 시점" 이며 user_id 를 포함한다.
 */
export async function pushSignUp({
  btnName,
  method,
}: {
  btnName: string;
  method: SignUpMethod;
}) {
  if (!markPushedOnce()) return;

  const userId = await fetchAuthUserId();

  pushGTMEvent(GTM_EVENT.SIGNUP, {
    btnname: toBtnName(btnName),
    method,
    ...(userId && { user_id: userId }),
  });
}

/**
 * 로그인 **진입** 버튼 — 시트 row 27 `click_btn`(이동).
 *
 * 로그인 완료(row 40 `click_login`)와 다르다. 진입 지점이 결제·회원가입·계정선택 등
 * 여러 화면에 흩어져 있고 전부 `window.location` 리다이렉트라 마크업 태깅이 어려워
 * 이동 직전에 직접 push 한다.
 */
export function pushLoginEntry({ inPopup = false } = {}) {
  pushGTMEvent(inPopup ? GTM_EVENT.POPUP : GTM_EVENT.BTN, {
    btnname: toBtnName('로그인'),
    hyperlink: AUTH_URLS.OIDC_AUTHORIZE,
  });
}

/**
 * 다이얼로그 내부의 이동 버튼 — 시트 row 29 `click_popup`.
 * `AuthDialog` 계열은 vaul Drawer 라 퍼블리싱 `InPopupContext` 자동 분기가 닿지 않는다.
 */
export function pushPopupNav(btnName: string, href: string) {
  pushGTMEvent(GTM_EVENT.POPUP, {
    btnname: toBtnName(btnName),
    hyperlink: href,
  });
}

/**
 * 시트 row 40 `click_login` — 로그인 성공 시점에 발사한다.
 *
 * `method` 는 싣지 않는다. OIDC authorize 의 `provider=fastfive-home` 은 클라이언트
 * 식별자일 뿐이고, 실제 로그인 수단은 인증 서버 화면에서 선택돼 앱으로 내려오지 않는다.
 * 백엔드가 로그인 응답에 provider 를 실어주면 여기에 한 줄 추가하면 된다.
 */
export async function pushLogin({ btnName }: { btnName: string }) {
  const userId = await fetchAuthUserId();

  pushGTMEvent(GTM_EVENT.LOGIN, {
    btnname: toBtnName(btnName),
    ...(userId && { user_id: userId }),
  });
}
